import type {
	FilterDrawerCollection,
	FilterDrawerState,
	FilterDrawerTouchedItems,
	FilterSections
} from '../../types';
import {
	getFilterMeta,
	getFilterValueLabel,
	isEmptyValue,
	type LabelledFilterItem
} from '../getFilterValueLabel/getFilterValueLabel';

export enum FilterTrackEvent {
	FilterApplied = 'FILTER_APPLIED',
	FilterCleared = 'FILTER_CLEARED',
	SortChanged = 'SORT_CHANGED',
	// A filter combination that matched nothing — a demand signal for stock we
	// don't carry, rather than a UI interaction.
	NoResults = 'FILTER_NO_RESULTS'
}

export interface FilterTrackEventPayload {
	event: FilterTrackEvent;
	props: Record<string, string | number>;
}

// Facet names (normalised) treated as the artist facet, so the artist value is
// surfaced as a dedicated `artist` property for downstream analytics.
export const ARTIST_FILTER_TYPES = ['artist', 'artists'];

const normalizeFilterType = (label: string) => label.trim().toLowerCase().replace(/\s+/g, '_');

// The facet's stable, human-meaningful name (e.g. 'artists', 'categories'),
// resolved from the drawer config because `filterCollectionId` is a numeric
// Directus id on CMS-driven pages (e.g. editions).
export const getFilterTypeName = ({
	filterSections,
	filterCollectionId
}: {
	filterSections: FilterSections;
	filterCollectionId: string;
}) => {
	const meta = getFilterMeta(filterSections, filterCollectionId);
	const item = meta?.filterType?.item as LabelledFilterItem | undefined;

	return normalizeFilterType(item?.label || meta?.section?.title || filterCollectionId);
};

// Resolves each applied facet to its reportable name and display value. Shared
// so the artist lookup and the event payloads can never disagree.
export const resolveFilterFacets = ({
	collections,
	filterSections
}: {
	collections: FilterDrawerCollection[];
	filterSections: FilterSections;
}) =>
	collections.map((collection) => {
		const meta = getFilterMeta(filterSections, collection.filterCollectionId);
		const item = meta?.filterType?.item as LabelledFilterItem | undefined;

		return {
			filterType: normalizeFilterType(
				item?.label || meta?.section?.title || collection.filterCollectionId
			),
			filterValue: getFilterValueLabel({ collection, item })
		};
	});

export const getFilterTrackEvents = ({
	filter,
	touchedFilterItems,
	filterSections
}: {
	filter: FilterDrawerState;
	touchedFilterItems: FilterDrawerTouchedItems;
	filterSections: FilterSections;
}): FilterTrackEventPayload[] => {
	const appliedCollections = Object.keys(touchedFilterItems)
		.map((filterCollectionId) => filter[filterCollectionId])
		.filter(
			(collection): collection is FilterDrawerCollection =>
				!!collection && !isEmptyValue(collection.value)
		);

	if (!appliedCollections.length) {
		return [
			{
				event: FilterTrackEvent.FilterCleared,
				props: { filter_type: 'all' }
			}
		];
	}

	const resolved = resolveFilterFacets({ collections: appliedCollections, filterSections });

	// The artist facet may be applied alongside another facet, so it is resolved
	// across all applied collections rather than only the one being reported.
	const artist = resolved.find(({ filterType }) =>
		ARTIST_FILTER_TYPES.includes(filterType)
	)?.filterValue;

	return resolved.map(({ filterType, filterValue }) => {
		const props: Record<string, string | number> = {
			filter_type: filterType,
			filter_value: filterValue,
			filter_count: resolved.length
		};

		if (artist) {
			props.artist = artist;
		}

		return {
			event: FilterTrackEvent.FilterApplied,
			props
		};
	});
};
